import React, { useState, useEffect, useCallback } from "react";
import {
    Paper,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Chip
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { investmentService } from "../investments/investmentService";
import { formatCurrency } from "../capital/capitalUtils";

const PortfolioAnalytics = () => {
    // eslint-disable-next-line no-unused-vars
    const { t, i18n } = useTranslation();
    const { currentUser } = useAuth();
    const [portfolioData, setPortfolioData] = useState({
        totalInvested: 0,
        totalValue: 0,
        totalReturn: 0,
        returnPercentage: 0,
        activeInvestments: 0,
        completedInvestments: 0
    });
    const [loading, setLoading] = useState(true);

    const loadPortfolioData = useCallback(async () => {
        try {
            setLoading(true);
            const data = await investmentService.getUserInvestments(currentUser.uid);
      
            // Calculate portfolio metrics
            const totalInvested = data.reduce((sum, inv) => sum + (inv.amount || 0), 0);
            const activeInvestments = data.filter(inv => inv.status === "pending").length;
            const completedInvestments = data.filter(inv => inv.status === "completed").length;
      
            // Mock calculation for total value and returns (in real app, this would come from actual performance data)
            const totalValue = totalInvested * 1.08; // 8% average return
            const totalReturn = totalValue - totalInvested;
            const returnPercentage = totalInvested > 0 ? (totalReturn / totalInvested) * 100 : 0;

            setPortfolioData({
                totalInvested,
                totalValue,
                totalReturn,
                returnPercentage,
                activeInvestments,
                completedInvestments
            });
        } catch (error) {
            // setError("Error loading portfolio data"); // Original code had this line commented out
        } finally {
            setLoading(false);
        }
    }, [currentUser?.uid]);

    useEffect(() => {
        if (currentUser) {
            loadPortfolioData();
        }
    }, [currentUser, loadPortfolioData]);

    if (loading) {
        return <Typography>Loading portfolio analytics...</Typography>;
    }

    return (
        <Paper sx={{ p: 4, borderRadius: 4, background: "#fff", boxShadow: "0 4px 24px 0 rgba(21, 82, 99, 0.10)" }}>
            <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#155263", borderBottom: "2px solid #DBC078", pb: 1, mb: 3 }}
            >
        Portfolio Analytics
            </Typography>

            {/* Summary Cards - Improved Layout */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4, justifyContent: "space-between" }}>
                {/* Total Portfolio Value */}
                <Box sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
                    <Card sx={{ height: "100%", bgcolor: "#f8f9fa", borderRadius: 3, boxShadow: "0 2px 12px 0 rgba(21,82,99,0.08)" }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Portfolio Value
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: "#155263" }}>
                                {formatCurrency(portfolioData.totalValue ?? 0, "EUR", i18n.language)}
                            </Typography>
                            <Chip 
                                label={`+${portfolioData.returnPercentage.toFixed(2)}%`}
                                color="success"
                                size="small"
                                sx={{ mt: 1 }}
                            />
                        </CardContent>
                    </Card>
                </Box>
                {/* Total Invested */}
                <Box sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
                    <Card sx={{ height: "100%", bgcolor: "#f8f9fa", borderRadius: 3, boxShadow: "0 2px 12px 0 rgba(21,82,99,0.08)" }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Invested
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: "#155263" }}>
                                {formatCurrency(portfolioData.totalInvested ?? 0, "EUR", i18n.language)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                {/* Total Returns */}
                <Box sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
                    <Card sx={{ height: "100%", bgcolor: "#f8f9fa", borderRadius: 3, boxShadow: "0 2px 12px 0 rgba(21,82,99,0.08)" }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Returns
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: portfolioData.totalReturn >= 0 ? "success.main" : "error.main" }}>
                                {formatCurrency(portfolioData.totalReturn ?? 0, "EUR", i18n.language)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
                {/* Active Investments */}
                <Box sx={{ flex: "1 1 220px", minWidth: 220, maxWidth: 320 }}>
                    <Card sx={{ height: "100%", bgcolor: "#f8f9fa", borderRadius: 3, boxShadow: "0 2px 12px 0 rgba(21,82,99,0.08)" }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                Active Investments
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: "#155263" }}>
                                {portfolioData.activeInvestments}
                            </Typography>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
            {/* End Summary Cards */}

            {/* Performance Chart Placeholder */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ color: "#155263", mb: 2 }}>
          Portfolio Performance
                </Typography>
                <Box sx={{ bgcolor: "#f8f9fa", p: 3, borderRadius: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
            Performance chart will be displayed here
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
            (Chart integration would show historical performance over time)
                    </Typography>
                </Box>
            </Box>

            {/* Risk Metrics */}
            <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ color: "#155263", mb: 2 }}>
          Risk Metrics
                </Typography>
                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                Portfolio Diversification
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={75} 
                                sx={{ height: 8, borderRadius: 4, bgcolor: "#e0e0e0" }}
                            />
                            <Typography variant="caption" color="text.secondary">
                75% diversified across different opportunities
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                Risk Level
                            </Typography>
                            <Chip label="Medium" color="warning" size="small" />
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Balanced portfolio with moderate risk
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </Paper>
    );
};

export default PortfolioAnalytics; 